import React, { useEffect, useState, useRef } from 'react';
import '../../styled/pages/dashboard.scss';
import { Icon } from '@ohif/ui';
import {
  CustomTable,
  Header,
  Pagination,
  Loader,
  ConfirmationModal,
  Success,
  Failed,
} from '../../components';
import { capitalizeFirstLetterOfEachWord, customStyles } from '../../Utils/utils';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getClinicApi } from '../../rtk-apis/clinics/getClinic';
import { clearState } from '../../store/slices/clinics/getClinic';
import { deleteClinicApi } from '../../rtk-apis/clinics/deleteClinic';
import { closeModal } from '../../store/slices/clinics/deleteClinic';
import useDebounce from '../../components/useDebounce';
import SortComponent from '../../components/SortComponent';

const ClinicsList = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const searchQuery = useDebounce(searchTerm, 1000);
  const prevPageRef = useRef(1);

  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  });

  const columns = [
    { key: 'name', title: 'Name' },
    { key: 'email', title: 'Email Address' },
    { key: 'adminName', title: 'Admin' },
    { key: 'contactDetails', title: 'Contact Details' },
    { key: 'Address', title: 'Location' },
  ];

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit-icon',
      onClick: (item: any) => {
        navigate(`/updateclinic/${item?._id}`, { state: { clinicDetails: item } });
      },
    },
    {
      label: 'Delete',
      iconName: 'delete-icon',
      onClick: (item: any) => {
        setSelectedRow(item?._id);
        setIsConfirmationModalOpen(true);
      },
    },
  ];

  // *********************** Redux States ************************************

  const dispatch = useDispatch<AppDispatch>();
  const { status, result, msg, loading } = useSelector(
    (state: RootState) => state.getClinicResponse
  );
  const deleteClinicResponse = useSelector((state: RootState) => state.deleteClinicResponse);

  // ******************** Use-Effects *******************************************

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  // Fetch data when pagination or search changes
  useEffect(() => {
    const payload = {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      search: searchQuery,
    };
    dispatch(getClinicApi(payload));
    prevPageRef.current = pagination.currentPage;
  }, [searchQuery, pagination.currentPage, pagination.pageSize]);

  // Refetch after successful delete
  useEffect(() => {
    if (deleteClinicResponse?.status === 'SUCCESS') {
      const payload = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        search: searchQuery,
      };
      dispatch(getClinicApi(payload));
    }
  }, [deleteClinicResponse?.status]);

  // Handle API response - THIS IS THE CRITICAL PART
  useEffect(() => {
    if (status === 'SUCCESS' && result?.data) {
      const modifiedResponse = result.data.map(item => {
        const adminName = capitalizeFirstLetterOfEachWord(item.adminDetails?.[0]?.name) || null;
        const contactDetails = item?.countryCode + ' ' + item?.contactDetails;
        return {
          ...item,
          adminName,
          contactDetails,
        };
      });
      // Force a clean state update
      setTableData([]);
      setTimeout(() => {
        setTableData(modifiedResponse);
      }, 0);

      // Update pagination from API response
      if (result?.pagination) {
        setPagination(prev => ({
          ...prev,
          currentPage: parseInt(result.pagination.currentPage, 10),
          pageSize: parseInt(result.pagination.pageSize, 10),
          totalRecords: parseInt(result.pagination.totalRecords, 10),
          totalPages: Math.ceil(
            parseInt(result.pagination.totalRecords, 10) / parseInt(result.pagination.pageSize, 10)
          ),
        }));
      }

      dispatch(clearState());
    }

    if (status === 'FAILED') {
      if (msg === 'No clinics found.') {
        setTableData([]);
        setPagination(prev => ({
          ...prev,
          totalRecords: 0,
          totalPages: 1,
          currentPage: 1,
        }));
      }
      dispatch(clearState());
    }
  }, [status, result]);

  // Handle delete response modals
  useEffect(() => {
    if (deleteClinicResponse?.status === 'SUCCESS') {
      setDisplayMessage(deleteClinicResponse?.msg);
      setIsSuccessModalOpen(true);
    }
    if (deleteClinicResponse?.status === 'ERROR') {
      setDisplayMessage(deleteClinicResponse?.msg);
      setIsFailedModalOpen(true);
    }
    dispatch(closeModal());
  }, [deleteClinicResponse.status]);

  const handleDeleteClinic = () => {
    const payload = {
      id: selectedRow,
    };
    dispatch(deleteClinicApi(payload));
    setSelectedRow(null);
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
    }));
  }, [searchQuery]);

  const handlePageChange = (page: number) => {
    // Clear table data immediately to show loading state
    setTableData([]);
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleItemsPerPageChange = (size: number) => {
    setTableData([]);
    setPagination(prev => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
    }));
  };

  // Handler for sort that doesn't interfere with pagination
  const handleSortedData = (sortedData: any[]) => {
    // Only update if we're not in the middle of a page change
    if (prevPageRef.current === pagination.currentPage && sortedData.length > 0) {
      setTableData(sortedData);
    }
  };

  return (
    <>
      <section
        className="layoutContainer"
        style={customStyles.layoutBackground}
      >
        <div className="layoutGrid">
          <div className="page page--fullBody">
            <Header />
            <div className="page__body">
              <div className="page__content">
                <div className="page__body__head">
                  <h1 className="title title--h6 title--normal">Manage Clinics</h1>
                  <div className="btn__group">
                    <button
                      type="button"
                      className="btn btn__primary"
                      onClick={() => {
                        navigate('/addclinic');
                      }}
                    >
                      <Icon name="add-icon" />
                      Add Clinic
                    </button>
                  </div>
                </div>
                <div className="tableWrap">
                  <div className="tableWrap__head">
                    <div className="formRow">
                      <div className="formCol relative">
                        <input
                          className="form__input form__input--search"
                          type="text"
                          placeholder="Search by Name"
                          value={searchTerm}
                          onChange={e => setSearchTerm(e?.target?.value)}
                        />
                        {searchTerm && (
                          <span
                            className="absolute right-8 bottom-2 cursor-pointer"
                            onClick={() => setSearchTerm('')}
                          >
                            <Icon name="clear-icon" />
                          </span>
                        )}
                        <span className="absolute right-4 bottom-3 cursor-pointer">
                          <Icon name="search-icon" />
                        </span>
                      </div>
                    </div>
                    <div className="btn__group">
                      <SortComponent
                        searchQuery={searchQuery}
                        data={tableData}
                        setFilteredData={handleSortedData}
                        sortKey="name"
                      />
                    </div>
                  </div>
                  <CustomTable
                    actions={actions}
                    columns={columns}
                    tableData={tableData}
                    key={`table-${pagination.currentPage}-${tableData.length}`}
                  />
                </div>
              </div>
            </div>
            {pagination.totalRecords > 0 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                itemsPerPage={pagination.pageSize}
                totalItems={pagination.totalRecords}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </div>
        </div>
      </section>
      <Loader isLoading={initialLoading || loading || deleteClinicResponse.loading} />
      <ConfirmationModal
        title="Delete Clinic"
        description="Are you sure you want to delete this clinic?"
        isConfirmationModalOpen={isConfirmationModalOpen}
        handleCloseModal={() => setIsConfirmationModalOpen(false)}
        handleConfirm={() => {
          setIsConfirmationModalOpen(false);
          handleDeleteClinic();
        }}
        cancelActionText="Cancel"
        confirmActionText="Yes"
        isPatientReport={true}
      />
      <Success
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
        handleCloseModal={() => setIsSuccessModalOpen(false)}
      />
      <Failed
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
        handleCloseModal={() => setIsFailedModalOpen(false)}
      />
    </>
  );
};

export default ClinicsList;
